import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { SectionPaper } from '../accounting-overview-components/layout';

// Mock transaction data - in production this would come from Supabase
const mockTransactions = {
  'First National': [
    { id: 1, date: 'Nov 22, 2024', description: 'Wire - vendor tooling', amount: -12400, type: 'debit', status: 'cleared', category: 'Vendor Payment', linkedInvoice: null },
    { id: 2, date: 'Nov 21, 2024', description: 'ACH Deposit - Customer Payment', amount: 45200, type: 'credit', status: 'cleared', category: 'Revenue', linkedInvoice: 'INV-2024-001' },
    { id: 3, date: 'Nov 20, 2024', description: 'Monthly Service Fee', amount: -150, type: 'debit', status: 'cleared', category: 'Bank Fees', linkedInvoice: null },
    { id: 4, date: 'Nov 19, 2024', description: 'Wire Transfer - Equipment Purchase', amount: -87500, type: 'debit', status: 'pending', category: 'Capital Expense', linkedInvoice: null },
    { id: 5, date: 'Nov 18, 2024', description: 'ACH Deposit - Customer Payment', amount: 28900, type: 'credit', status: 'cleared', category: 'Revenue', linkedInvoice: 'INV-2024-002' }
  ],
  'Mercury Treasury': [
    { id: 6, date: 'Nov 22, 2024', description: 'Interest Payment', amount: 1250, type: 'credit', status: 'cleared', category: 'Interest', linkedInvoice: null },
    { id: 7, date: 'Nov 21, 2024', description: 'FX Conversion - EUR to USD', amount: 22000, type: 'credit', status: 'cleared', category: 'FX', linkedInvoice: null },
    { id: 8, date: 'Nov 20, 2024', description: 'Treasury Transfer Out', amount: -50000, type: 'debit', status: 'cleared', category: 'Transfer', linkedInvoice: null }
  ],
  'Silicon Hills': [
    { id: 9, date: 'Nov 21, 2024', description: 'Payroll Debit - Bi-weekly', amount: -49300, type: 'debit', status: 'cleared', category: 'Payroll', linkedInvoice: null },
    { id: 10, date: 'Nov 7, 2024', description: 'Payroll Debit - Bi-weekly', amount: -49300, type: 'debit', status: 'cleared', category: 'Payroll', linkedInvoice: null }
  ],
  'Stripe Capital': [
    { id: 11, date: 'Nov 21, 2024', description: 'Card Payout', amount: 8760, type: 'credit', status: 'pending', category: 'Revenue', linkedInvoice: null },
    { id: 12, date: 'Nov 20, 2024', description: 'Card Payout', amount: 12450, type: 'credit', status: 'cleared', category: 'Revenue', linkedInvoice: 'INV-2024-003' },
    { id: 13, date: 'Nov 19, 2024', description: 'Stripe Processing Fee', amount: -320, type: 'debit', status: 'cleared', category: 'Processing Fees', linkedInvoice: null }
  ]
};

// Mock invoices for linking
const mockInvoices = [
  { id: 'INV-2024-001', customer: 'Glow Beauty Retailers', amount: 45200, status: 'paid' },
  { id: 'INV-2024-002', customer: 'Spa Collective', amount: 28900, status: 'paid' },
  { id: 'INV-2024-003', customer: 'Luxe Labs', amount: 12450, status: 'paid' },
  { id: 'INV-2024-004', customer: 'Costa Resorts', amount: 63250, status: 'pending' },
  { id: 'INV-2024-005', customer: 'Beauty World', amount: 18900, status: 'pending' }
];

const BankAccountTransactions = ({ account, onBack }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');

  const transactions = mockTransactions[account.bank] || [];

  const filteredTransactions = transactions.filter((tx) =>
    tx.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLinkInvoice = (transaction) => {
    setSelectedTransaction(transaction);
    setSelectedInvoiceId(transaction.linkedInvoice || '');
    setLinkDialogOpen(true);
  };

  const handleSaveLink = () => {
    // In production, this would update the transaction in Supabase
    console.log(`Linking transaction ${selectedTransaction.id} to invoice ${selectedInvoiceId}`);
    setLinkDialogOpen(false);
    setSelectedTransaction(null);
    setSelectedInvoiceId('');
  };

  const handleUnlink = () => {
    setSelectedInvoiceId('');
  };

  const formatAmount = (amount) => {
    const formatted = Math.abs(amount).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return amount >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const getLinkedInvoice = (invoiceId) => {
    return mockInvoices.find(inv => inv.id === invoiceId);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={onBack} sx={{ color: theme.palette.primary.main }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 400, color: 'primary.main' }}>
            {account.bank} - {account.type}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Account Balance: {account.balance} • {transactions.length} transactions
          </Typography>
        </Box>
      </Stack>

      <SectionPaper sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                background: 'white',
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Stack>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Linked Invoice</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.map((tx) => {
              const linkedInvoice = tx.linkedInvoice ? getLinkedInvoice(tx.linkedInvoice) : null;
              return (
                <TableRow key={tx.id} hover>
                  <TableCell>{tx.date}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {tx.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tx.category}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 500,
                      color: tx.amount >= 0 ? theme.palette.success.main : theme.palette.error.main
                    }}
                  >
                    {formatAmount(tx.amount)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tx.status}
                      size="small"
                      color={tx.status === 'cleared' ? 'success' : 'warning'}
                      variant="outlined"
                      sx={{ borderRadius: 2, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    {linkedInvoice ? (
                      <Chip
                        icon={<ReceiptIcon fontSize="small" />}
                        label={tx.linkedInvoice}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                        onClick={() => handleLinkInvoice(tx)}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not linked
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Link Invoice">
                      <IconButton
                        size="small"
                        onClick={() => handleLinkInvoice(tx)}
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </SectionPaper>

      {/* Link Invoice Dialog */}
      <Dialog
        open={linkDialogOpen}
        onClose={() => {
          setLinkDialogOpen(false);
          setSelectedTransaction(null);
          setSelectedInvoiceId('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <LinkIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 400 }}>
              Link Invoice to Transaction
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Transaction Details:
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                  backgroundColor: alpha(theme.palette.grey[50], 0.7)
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 400, mb: 0.5 }}>
                  {selectedTransaction.description}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Date: {selectedTransaction.date}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Amount: {formatAmount(selectedTransaction.amount)}
                  </Typography>
                </Stack>
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            select
            label="Select Invoice"
            value={selectedInvoiceId}
            onChange={(e) => setSelectedInvoiceId(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">
              <em>None (Unlink)</em>
            </MenuItem>
            {mockInvoices.map((invoice) => (
              <MenuItem key={invoice.id} value={invoice.id}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {invoice.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {invoice.customer} • {invoice.amount.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0
                      })}
                    </Typography>
                  </Box>
                  <Chip
                    label={invoice.status}
                    size="small"
                    color={invoice.status === 'paid' ? 'success' : 'warning'}
                    variant="outlined"
                    sx={{ borderRadius: 2, textTransform: 'capitalize' }}
                  />
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          {selectedInvoiceId && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.05)
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selected Invoice:
              </Typography>
              {(() => {
                const invoice = mockInvoices.find(inv => inv.id === selectedInvoiceId);
                return invoice ? (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                      {invoice.id} - {invoice.customer}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Amount: {invoice.amount.toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0
                      })}
                    </Typography>
                  </Stack>
                ) : null;
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setLinkDialogOpen(false);
              setSelectedTransaction(null);
              setSelectedInvoiceId('');
            }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveLink}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Save Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankAccountTransactions;

